<template>
  <!-- Bento grid (S2_Bento): tiles of different sizes — a photo, a number, a
       review, an offer, where we work, a badge or plain words. Folds to two
       across on phones. -->
  <SectionBand :heading="section.heading || section.title" :variant="variant" :background="section.background" label="At a glance">
    <div class="s2-bento mo-stagger">
      <component :is="href(t) ? 'a' : 'div'" v-for="(t, i) in tiles" :key="i" :href="href(t) ? back(href(t)) : null"
                 class="s2-tile" :class="[`s2-tile--${t.kind}`, i === 0 && t.kind === 'photo' ? 's2-tile--big' : '']">
        <template v-if="t.kind === 'photo'">
          <Media :media="t.image" class="s2-tile__bg" />
          <span class="s2-tile__over"><span v-if="t.label" class="t-eyebrow">{{ t.label }}</span><strong>{{ t.title || t.text }}</strong></span>
        </template>
        <template v-else-if="t.kind === 'stat'">
          <span class="t-eyebrow">{{ t.label }}</span>
          <strong class="s2-tile__num" :class="{'mo-count': isNum(t.value)}" :data-to="isNum(t.value) ? numeric(t.value) : null" :data-suffix="suffix(t.value)">{{ t.value }}</strong>
          <span v-if="t.text" class="t-muted">{{ t.text }}</span>
        </template>
        <template v-else-if="t.kind === 'review'">
          <span class="s2-stars" :aria-label="`${stars(t)} out of 5`">{{ '★'.repeat(stars(t)) }}</span>
          <p class="s2-tile__quote">“{{ t.text || t.body }}”</p>
          <span class="t-muted">{{ t.name }}<template v-if="t.town">, {{ t.town }}</template></span>
        </template>
        <template v-else>
          <span v-if="t.label" class="t-eyebrow">{{ t.label }}</span>
          <strong class="t-h3">{{ t.title || t.value }}</strong>
          <span v-if="t.text" class="t-muted">{{ t.text }}</span>
          <span v-if="href(t)" class="t-link s2-arrow">{{ t.button || 'Find out more' }}</span>
        </template>
      </component>
    </div>
  </SectionBand>
</template>

<script setup>
import {computed} from "vue";
import SectionBand from "../shared/SectionBand.vue";
import Media from "../shared/Media.vue";
import {safeLink} from "@/utils/blocks.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();

const props = defineProps({section: {type: Object, required: true}, variant: {type: [Number, String], default: 1}});
const KINDS = ["photo", "stat", "review", "offer", "map", "badge", "words", "text"];
const tiles = computed(() => (Array.isArray(props.section.tiles) ? props.section.tiles : [])
  .filter((t) => t && typeof t === "object")
  .map((t) => ({...t, kind: KINDS.includes(t.kind) ? t.kind : (t.image ? "photo" : "text")}))
  .filter((t) => t.kind !== "photo" || t.image)
  .slice(0, 8));
const href = (t) => safeLink(t.link || "");
const isNum = (v) => /^\s*[\d,.]+\s*[+%★]?\s*$/.test(String(v ?? "")) && String(v ?? "").trim() !== "";
const numeric = (v) => String(v).replace(/[^\d.]/g, "");
const suffix = (v) => (String(v ?? "").match(/[+%★]\s*$/) || [""])[0].trim() || null;
const stars = (t) => Math.max(1, Math.min(5, Number(t.rating) || 5));
</script>

<style scoped>
.s2-bento { display: grid; gap: var(--t-gap); grid-template-columns: repeat(4, minmax(0, 1fr)); grid-auto-rows: minmax(150px, auto); }
.s2-tile { position: relative; display: flex; flex-direction: column; justify-content: flex-end; gap: .35rem; padding: 1.1rem 1.2rem; border-radius: var(--t-card-radius, var(--t-radius)); background: var(--t-card-bg, var(--t-surface)); border: var(--t-card-border, 1px solid var(--t-line)); box-shadow: var(--t-card-shadow); color: inherit; text-decoration: none; overflow: hidden; }
.s2-tile--big { grid-column: span 2; grid-row: span 2; }
.s2-tile--photo { padding: 0; color: #fff; min-height: 200px; }
.s2-tile__bg { position: absolute; inset: 0; }
.s2-tile__bg :deep(img) { height: 100%; width: 100%; object-fit: cover; border-radius: 0; }
.s2-tile__over { position: relative; padding: 1.1rem 1.2rem; background: linear-gradient(transparent, rgba(0,0,0,.65)); display: flex; flex-direction: column; gap: .2rem; }
.s2-tile__over .t-eyebrow { color: #fff; opacity: .85; }
.s2-tile--stat { background: var(--t-primary); color: var(--t-primary-ink); border: 0; }
.s2-tile--stat .t-eyebrow, .s2-tile--stat .t-muted { color: inherit; opacity: .8; }
.s2-tile__num { font-family: var(--t-font-display); font-size: clamp(2.2rem, 4vw, 3.2rem); line-height: 1; font-weight: var(--t-display-weight, 800); }
.s2-tile--offer { background: color-mix(in srgb, var(--t-accent) 22%, var(--t-surface)); }
.s2-tile--badge { background: var(--t-surface-2); }
.s2-tile__quote { margin: 0; font-size: 1rem; line-height: 1.4; }
.s2-stars { color: var(--t-accent); letter-spacing: .1em; }
.s2-arrow::after { content: " →"; }
@media (max-width: 760px) {
  .s2-bento { grid-template-columns: repeat(2, minmax(0, 1fr)); grid-auto-rows: minmax(130px, auto); }
  .s2-tile--big { grid-column: span 2; grid-row: span 1; min-height: 220px; }
}
</style>
